import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  DollarSign,
  Clock,
  Users,
  Calendar,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Send,
  Plus,
  ChevronRight,
  ChevronLeft,
  Target,
  TrendingUp,
  Wallet,
  Receipt,
  Check,
  X,
  Loader2,
  MapPin,
  LayoutGrid,
  BarChart3,
  CreditCard,
  HardHat,
  AlertCircle,
  Settings,
  Camera,
  Wrench,
  Upload,
  Truck,
  Building2,
  Package,
  FileCheck,
  UserCircle,
  CheckSquare,
  Edit3,
} from "lucide-react";
import { toast } from "sonner";
import { 
  UpdateProgressModal, 
  AddMilestoneModal, 
  RecordClientPaymentModal, 
  AddCostModal,
  AssignCrewModal,
  EditMilestoneModal,
  BulkEditMilestoneModal
} from "../components/ProjectModals";
import { usePermissions } from "../contexts/PermissionsContext";
import PurchaseOrdersTab from "../components/PurchaseOrdersTab";
import FieldIntelTab from "../components/FieldIntelTab";

// Import refactored utilities and components
import { formatCurrency, formatDate, formatShortDate, calculateDays, getDaysUntil } from './project-detail/utils';
import { ScheduleStatusBadge, MilestoneStatusBadge, PaymentStatusBadge, MetricCard } from './project-detail/components';

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// ================= GANTT TIMELINE COMPONENT =================

const GanttTimeline = ({ milestones, projectStart, projectEnd, onMilestoneClick, onBulkEdit, viewMode = 'week' }) => {
  const [selectedMilestones, setSelectedMilestones] = React.useState([]);
  
  if (!milestones || milestones.length === 0) {
    return (
      <div className="p-8 text-center text-slate-500">
        <Target className="h-12 w-12 mx-auto mb-4 text-slate-600" />
        <p>No milestones yet</p>
      </div>
    );
  }
  
  // Toggle milestone selection
  const toggleSelection = (milestoneId, e) => {
    e.stopPropagation();
    setSelectedMilestones(prev => 
      prev.includes(milestoneId) 
        ? prev.filter(id => id !== milestoneId)
        : [...prev, milestoneId]
    );
  };
  
  // Select/deselect all
  const toggleSelectAll = () => {
    if (selectedMilestones.length === milestones.length) {
      setSelectedMilestones([]);
    } else {
      setSelectedMilestones(milestones.map(m => m.milestone_id));
    }
  };
  
  // Get selected milestone objects
  const getSelectedMilestoneObjects = () => {
    return milestones.filter(m => selectedMilestones.includes(m.milestone_id));
  };
  
  // Generate date labels based on viewMode
  const start = new Date(projectStart || new Date());
  const end = new Date(projectEnd || new Date());
  const totalDays = Math.max(calculateDays(projectStart, projectEnd), 30);
  
  // Configure intervals based on view mode
  let numMarkers, dateFormat;
  switch(viewMode) {
    case 'month':
      numMarkers = 12;
      dateFormat = (date) => date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      break;
    case 'quarter':
      numMarkers = 4;
      dateFormat = (date) => `Q${Math.floor(date.getMonth() / 3) + 1} '${date.getFullYear().toString().slice(-2)}`;
      break;
    default: // week
      numMarkers = 8;
      dateFormat = (date) => formatShortDate(date.toISOString());
  }
  
  // Generate date markers
  const dateMarkers = [];
  const interval = Math.ceil(totalDays / (numMarkers - 1));
  for (let i = 0; i < numMarkers; i++) {
    const date = new Date(start);
    date.setDate(date.getDate() + (i * interval));
    dateMarkers.push({
      label: dateFormat(date),
      position: (i * interval / totalDays) * 100
    });
  }
  
  return (
    <div className="h-full overflow-y-auto pr-2" style={{ minHeight: '500px', scrollbarWidth: 'thin', scrollbarColor: '#475569 #1e293b' }}>
      {/* Bulk Action Toolbar - Appears when items selected */}
      {selectedMilestones.length > 0 && (
        <div className="sticky top-0 z-20 mb-4 p-3 rounded-xl bg-emerald-600/20 border border-emerald-500/30 backdrop-blur-sm flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <CheckSquare className="h-5 w-5 text-emerald-400" />
              <span className="text-emerald-400 font-bold">{selectedMilestones.length}</span>
              <span className="text-slate-300">milestone{selectedMilestones.length > 1 ? 's' : ''} selected</span>
            </div>
            <button 
              onClick={() => setSelectedMilestones([])}
              className="text-sm text-slate-400 hover:text-white underline"
            >
              Clear selection
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onBulkEdit && onBulkEdit(getSelectedMilestoneObjects())}
              className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold flex items-center gap-2 transition-all"
              data-testid="bulk-edit-btn"
            >
              <Edit3 className="h-4 w-4" />
              Bulk Edit
            </button>
          </div>
        </div>
      )}
      
      {/* Date Header Row */}
      <div className="flex items-center gap-4 pb-4 border-b border-slate-700 sticky top-0 bg-slate-900/95 backdrop-blur-sm z-10">
        {/* Select All Checkbox */}
        <div className="w-8 flex-shrink-0 flex items-center justify-center">
          <button
            onClick={toggleSelectAll}
            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
              selectedMilestones.length === milestones.length 
                ? 'bg-emerald-500 border-emerald-500' 
                : selectedMilestones.length > 0 
                  ? 'bg-emerald-500/50 border-emerald-500' 
                  : 'border-slate-600 hover:border-slate-500'
            }`}
            title={selectedMilestones.length === milestones.length ? "Deselect all" : "Select all"}
          >
            {selectedMilestones.length > 0 && <Check className="h-3 w-3 text-white" />}
          </button>
        </div>
        <div className="w-48 flex-shrink-0 text-sm font-semibold text-slate-400 uppercase tracking-wider">Task / Crew</div>
        <div className="flex-1 relative h-8">
          {dateMarkers.map((marker, idx) => (
            <span 
              key={idx}
              className="absolute text-sm font-medium text-slate-400 transform -translate-x-1/2"
              style={{ left: `${Math.min(marker.position, 95)}%` }}
            >
              {marker.label}
            </span>
          ))}
        </div>
        <div className="w-32 flex-shrink-0 pr-4 text-sm font-semibold text-slate-400 uppercase tracking-wider text-right">Budget</div>
      </div>
      
      {/* Milestone Rows */}
      {milestones.map((milestone, idx) => {
        const startOffset = calculateDays(projectStart, milestone.start_date);
        const duration = calculateDays(milestone.start_date, milestone.target_date) || 1;
        const left = Math.max(0, (startOffset / totalDays) * 100);
        const width = Math.min((duration / totalDays) * 100, 100 - left);
        
        const today = new Date();
        const targetDate = new Date(milestone.target_date);
        const daysLate = milestone.status !== 'complete' && today > targetDate 
          ? Math.floor((today - targetDate) / (1000 * 60 * 60 * 24)) 
          : 0;
        
        const progress = milestone.progress_percent || 0;
        const actualCost = milestone.actual_cost || 0;
        const budgetedCost = milestone.estimated_cost || 0;
        const isOverBudget = actualCost > budgetedCost;
        const isSelected = selectedMilestones.includes(milestone.milestone_id);
        
        // Determine bar style and status text based on status
        let barBg, barGradient, statusText, statusBadgeColor;
        if (milestone.status === 'complete') {
          barBg = 'bg-gradient-to-r from-emerald-600 to-emerald-500';
          statusText = 'Complete';
          statusBadgeColor = 'bg-emerald-500/20 text-emerald-400';
        } else if (milestone.status === 'in_progress' && daysLate > 0) {
          barBg = 'bg-gradient-to-r from-red-600 to-red-500';
          statusText = `${progress}% • ${daysLate}d late`;
          statusBadgeColor = 'bg-red-500/20 text-red-400';
        } else if (milestone.status === 'in_progress') {
          barBg = 'bg-gradient-to-r from-blue-600 to-blue-500';
          statusText = `${progress}%`;
          statusBadgeColor = 'bg-blue-500/20 text-blue-400';
        } else {
          barBg = 'bg-slate-700/50';
          statusText = 'Not Started';
          statusBadgeColor = 'bg-slate-600/30 text-slate-500';
        }
        
        return (
          <div 
            key={milestone.milestone_id} 
            className={`flex items-center gap-4 py-6 border-b border-slate-800/50 hover:bg-slate-800/30 cursor-pointer transition-all group rounded-xl my-2 ${
              isSelected ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-slate-900/30'
            }`}
            onClick={() => onMilestoneClick && onMilestoneClick(milestone)}
            data-testid={`milestone-row-${milestone.milestone_id}`}
          >
            {/* Checkbox */}
            <div className="w-8 flex-shrink-0 flex items-center justify-center">
              <button
                onClick={(e) => toggleSelection(milestone.milestone_id, e)}
                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                  isSelected 
                    ? 'bg-emerald-500 border-emerald-500' 
                    : 'border-slate-600 hover:border-emerald-500 group-hover:border-slate-500'
                }`}
                data-testid={`milestone-checkbox-${milestone.milestone_id}`}
              >
                {isSelected && <Check className="h-3 w-3 text-white" />}
              </button>
            </div>
            
            {/* Milestone Name - Larger text */}
            <div className="w-48 flex-shrink-0">
              <p className="text-base font-bold text-white truncate group-hover:text-emerald-400 transition-colors">{milestone.title}</p>
              <p className="text-sm text-slate-500 truncate mt-1">{milestone.assigned_sub_name || 'TBD'}</p>
            </div>
            
            {/* Gantt Bar with Progress and Status - MUCH TALLER */}
            <div className="flex-1 h-16 bg-slate-800/60 rounded-xl relative overflow-hidden border border-slate-700/30">
              {/* Background track line */}
              <div className="absolute inset-0 border border-slate-700/50 rounded-xl"></div>
              
              {/* Progress Bar - Taller and more prominent */}
              <div 
                className={`absolute top-1 bottom-1 rounded-lg ${barBg} shadow-lg transition-all duration-300 flex items-center justify-center`}
                style={{ left: `${left}%`, width: `${Math.max(width, 15)}%` }}
              >
                {/* Inner progress indicator for in-progress items */}
                {milestone.status === 'in_progress' && progress > 0 && progress < 100 && (
                  <div 
                    className="absolute top-0 left-0 bottom-0 bg-white/20 rounded-l-lg"
                    style={{ width: `${progress}%` }}
                  />
                )}
                
                {/* Status text inside bar - Larger */}
                <span className="px-3 text-sm font-bold text-white drop-shadow-lg truncate text-center">
                  {statusText}
                </span>
              </div>
              
              {/* Today marker line */}
              {(() => {
                const todayOffset = calculateDays(projectStart, new Date().toISOString());
                const todayPosition = (todayOffset / totalDays) * 100;
                if (todayPosition >= 0 && todayPosition <= 100) {
                  return (
                    <div 
                      className="absolute top-0 bottom-0 w-0.5 bg-amber-400 z-10"
                      style={{ left: `${todayPosition}%` }}
                    />
                  );
                }
                return null;
              })()}
            </div>
            
            {/* Cost Info - More Prominent and Better Styled */}
            <div className="w-32 flex-shrink-0 text-right pr-4">
              <div className={`text-lg font-bold ${
                milestone.status === 'complete' 
                  ? 'text-emerald-400' 
                  : isOverBudget 
                    ? 'text-red-400' 
                    : 'text-white'
              }`}>
                {formatCurrency(actualCost)}
              </div>
              <div className="text-sm text-slate-500">
                / {formatCurrency(budgetedCost)}
              </div>
            </div>
          </div>
        );
      })}
      
      {/* Legend */}
      <div className="flex items-center justify-center gap-6 pt-4 mt-2 border-t border-slate-800/50">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-emerald-500"></div>
          <span className="text-xs text-slate-400">Complete</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-blue-500"></div>
          <span className="text-xs text-slate-400">In Progress</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-red-500"></div>
          <span className="text-xs text-slate-400">Late</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-slate-600 border border-dashed border-slate-500"></div>
          <span className="text-xs text-slate-400">Not Started</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-0.5 bg-amber-400"></div>
          <span className="text-xs text-slate-400">Today</span>
        </div>
      </div>
    </div>
  );
};

// ================= ACTION ITEMS COMPONENT =================

const ActionItems = ({ milestones, project }) => {
  // Generate action items based on milestones
  const items = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  milestones.forEach(m => {
    if (m.status !== 'complete') {
      const daysUntil = getDaysUntil(m.target_date);
      
      if (daysUntil < 0) {
        items.push({
          type: 'overdue',
          label: 'OVERDUE:',
          title: `Schedule ${m.title.toLowerCase()} inspection`,
          due: m.target_date,
          daysText: `${Math.abs(daysUntil)} ${Math.abs(daysUntil) === 1 ? 'day' : 'days'} overdue`
        });
      } else if (daysUntil === 0) {
        items.push({
          type: 'today',
          label: 'TODAY:',
          title: `Confirm ${m.title.toLowerCase()} delivery`,
          due: m.target_date,
          daysText: 'today'
        });
      } else if (daysUntil <= 14) {
        items.push({
          type: 'upcoming',
          label: 'UPCOMING:',
          title: `Order ${m.title.toLowerCase()} supplies`,
          due: m.target_date,
          daysText: `${daysUntil} ${daysUntil === 1 ? 'day' : 'days'}`
        });
      }
    }
  });
  
  // Sort: overdue first, then today, then upcoming
  const order = { overdue: 0, today: 1, upcoming: 2 };
  items.sort((a, b) => order[a.type] - order[b.type]);
  
  if (items.length === 0) {
    return (
      <div className="p-6 text-center text-slate-500">
        <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
        <p>All caught up!</p>
      </div>
    );
  }
  
  return (
    <div className="p-4 space-y-3">
      {items.slice(0, 5).map((item, idx) => (
        <div key={idx} className="flex items-center gap-4 p-4 rounded-lg bg-slate-800/30 hover:bg-slate-800/50 transition-colors">
          {/* Status Dot */}
          <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
            item.type === 'overdue' ? 'bg-red-500' :
            item.type === 'today' ? 'bg-amber-500' :
            'bg-emerald-500'
          }`} />
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white">
              <span className={`font-bold mr-1 ${
                item.type === 'overdue' ? 'text-red-400' :
                item.type === 'today' ? 'text-amber-400' :
                'text-emerald-400'
              }`}>{item.label}</span>
              {item.title}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">Due: {formatDate(item.due)}</p>
          </div>
          
          {/* Days Badge */}
          <span className={`text-xs font-medium px-3 py-1.5 rounded ${
            item.type === 'overdue' ? 'bg-red-500/20 text-red-400' :
            item.type === 'today' ? 'bg-amber-500/20 text-amber-400' :
            'bg-emerald-500/20 text-emerald-400'
          }`}>
            {item.daysText}
          </span>
        </div>
      ))}
    </div>
  );
};

// ================= CALENDAR VIEW COMPONENT =================

const CalendarView = ({ milestones, paymentSchedule, project, onDayClick }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [selectedDay, setSelectedDay] = useState(null);
  
  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  
  const dayNames = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const fullDayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  
  // Time slots for week/day views
  const timeSlots = [];
  for (let hour = 6; hour <= 20; hour++) {
    timeSlots.push({
      hour,
      label: hour === 12 ? '12 PM' : hour < 12 ? `${hour} AM` : `${hour - 12} PM`
    });
  }
  
  // Get first day of month and number of days
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  const startingDayOfWeek = firstDayOfMonth.getDay();
  const daysInMonth = lastDayOfMonth.getDate();
  
  // Get week dates for week view
  const getWeekDates = () => {
    const dates = [];
    const dayOfWeek = currentDate.getDay();
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - dayOfWeek);
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      dates.push(date);
    }
    return dates;
  };
  
  // Navigation functions
  const navigatePrev = () => {
    if (viewMode === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    } else if (viewMode === 'week') {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() - 7);
      setCurrentDate(newDate);
    } else {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() - 1);
      setCurrentDate(newDate);
    }
  };
  
  const navigateNext = () => {
    if (viewMode === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    } else if (viewMode === 'week') {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() + 7);
      setCurrentDate(newDate);
    } else {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() + 1);
      setCurrentDate(newDate);
    }
  };
  
  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDay(new Date().getDate());
  };
  
  // Generate events for a specific date
  const getEventsForDate = (date) => {
    const events = [];
    const day = date.getDate();
    const month = date.getMonth();
    const year = date.getFullYear();
    
    // Check milestones
    milestones.forEach(m => {
      const startDate = new Date(m.start_date);
      const targetDate = new Date(m.target_date);
      
      if (startDate.getDate() === day && startDate.getMonth() === month && startDate.getFullYear() === year) {
        events.push({
          type: 'milestone_start',
          label: `${m.title} Start`,
          shortLabel: `${m.title.split(' ').slice(0, 2).join(' ')} Start`,
          color: 'bg-teal-500',
          time: '8:00 AM',
          milestone: m
        });
      }
      
      if (targetDate.getDate() === day && targetDate.getMonth() === month && targetDate.getFullYear() === year) {
        events.push({
          type: 'milestone_end',
          label: m.status === 'complete' ? `${m.title} Complete` : `${m.title} Due`,
          shortLabel: m.status === 'complete' ? `${m.title.split(' ').slice(0, 2).join(' ')} Done` : `${m.title.split(' ').slice(0, 2).join(' ')} Due`,
          color: m.status === 'complete' ? 'bg-emerald-500' : 'bg-teal-500',
          time: '5:00 PM',
          milestone: m
        });
      }
      
      if (m.assigned_sub_name && startDate.getDate() === day && startDate.getMonth() === month && startDate.getFullYear() === year) {
        events.push({
          type: 'crew',
          label: `Crew: ${m.assigned_sub_name}`,
          shortLabel: m.assigned_sub_name,
          color: 'bg-purple-500',
          time: '7:00 AM'
        });
      }
    });
    
    // Check payments
    paymentSchedule.forEach(p => {
      const dueDate = new Date(p.due_date);
      if (dueDate.getDate() === day && dueDate.getMonth() === month && dueDate.getFullYear() === year) {
        events.push({
          type: 'payment',
          label: p.type === 'down_payment' ? 'Down Payment Due' : p.type === 'final_payment' ? 'Final Payment Due' : 'Payment Due',
          shortLabel: p.type === 'down_payment' ? 'Down Payment' : p.type === 'final_payment' ? 'Final Payment' : 'Payment Due',
          color: 'bg-amber-500',
          time: '12:00 PM',
          amount: p.amount
        });
      }
    });
    
    return events;
  };
  
  // Legacy function for month view compatibility
  const getEventsForDay = (day) => {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return getEventsForDate(date);
  };
  
  // Generate calendar grid for month view
  const calendarDays = [];
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }
  
  const handleDayClick = (day, date = null) => {
    if (day || date) {
      if (date) {
        setCurrentDate(date);
        setSelectedDay(date.getDate());
        const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        onDayClick && onDayClick(dateStr, getEventsForDate(date));
      } else {
        setSelectedDay(day);
        const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        onDayClick && onDayClick(dateStr, getEventsForDay(day));
      }
    }
  };
  
  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() && 
           date.getMonth() === today.getMonth() && 
           date.getFullYear() === today.getFullYear();
  };
  
  // Get header title based on view mode
  const getHeaderTitle = () => {
    if (viewMode === 'month') {
      return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    } else if (viewMode === 'week') {
      const weekDates = getWeekDates();
      const startMonth = monthNames[weekDates[0].getMonth()];
      const endMonth = monthNames[weekDates[6].getMonth()];
      if (startMonth === endMonth) {
        return `${startMonth} ${weekDates[0].getDate()} - ${weekDates[6].getDate()}, ${weekDates[0].getFullYear()}`;
      }
      return `${startMonth} ${weekDates[0].getDate()} - ${endMonth} ${weekDates[6].getDate()}, ${weekDates[6].getFullYear()}`;
    } else {
      return `${fullDayNames[currentDate.getDay()]}, ${monthNames[currentDate.getMonth()]} ${currentDate.getDate()}, ${currentDate.getFullYear()}`;
    }
  };
  
  // Render Week View
  const renderWeekView = () => {
    const weekDates = getWeekDates();
    
    return (
      <div className="bg-slate-900/50 rounded-xl border border-slate-800 overflow-hidden">
        {/* Week Header */}
        <div className="grid grid-cols-8 border-b border-slate-800">
          <div className="p-3 text-center text-xs font-bold text-slate-600 border-r border-slate-800">
            TIME
          </div>
          {weekDates.map((date, idx) => (
            <div 
              key={idx} 
              className={`p-3 text-center border-r border-slate-800/50 last:border-r-0 cursor-pointer hover:bg-slate-800/50 transition-colors ${
                isToday(date) ? 'bg-emerald-500/10' : ''
              }`}
              onClick={() => handleDayClick(null, date)}
            >
              <div className="text-xs font-bold text-slate-500 uppercase">{dayNames[idx]}</div>
              <div className={`text-lg font-bold mt-1 ${
                isToday(date) ? 'w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center mx-auto' : 'text-slate-300'
              }`}>
                {date.getDate()}
              </div>
            </div>
          ))}
        </div>
        
        {/* Time Grid */}
        <div className="max-h-[500px] overflow-y-auto">
          {timeSlots.map((slot, slotIdx) => (
            <div key={slotIdx} className="grid grid-cols-8 border-b border-slate-800/30">
              <div className="p-2 text-xs text-slate-500 text-right pr-3 border-r border-slate-800 bg-slate-900/30">
                {slot.label}
              </div>
              {weekDates.map((date, dayIdx) => {
                const dayEvents = getEventsForDate(date);
                const slotEvents = dayEvents.filter(e => {
                  const eventHour = parseInt(e.time?.split(':')[0]) || 8;
                  const isPM = e.time?.includes('PM');
                  const hour24 = isPM && eventHour !== 12 ? eventHour + 12 : eventHour;
                  return hour24 === slot.hour;
                });
                
                return (
                  <div 
                    key={dayIdx}
                    className={`min-h-[50px] p-1 border-r border-slate-800/30 last:border-r-0 hover:bg-slate-800/30 cursor-pointer transition-colors ${
                      isToday(date) ? 'bg-emerald-500/5' : ''
                    }`}
                    onClick={() => handleDayClick(null, date)}
                  >
                    {slotEvents.map((event, eIdx) => (
                      <div 
                        key={eIdx}
                        className={`text-xs px-2 py-1 rounded mb-1 text-white truncate ${event.color}`}
                        title={event.label}
                      >
                        {event.shortLabel}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  // Render Day View
  const renderDayView = () => {
    const dayEvents = getEventsForDate(currentDate);
    
    return (
      <div className="bg-slate-900/50 rounded-xl border border-slate-800 overflow-hidden">
        {/* Day Header with summary */}
        <div className="p-4 border-b border-slate-800 bg-slate-800/30">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-lg font-bold text-white">{fullDayNames[currentDate.getDay()]}</h4>
              <p className="text-sm text-slate-400">{monthNames[currentDate.getMonth()]} {currentDate.getDate()}, {currentDate.getFullYear()}</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-400">{dayEvents.length}</div>
                <div className="text-xs text-slate-500">Events</div>
              </div>
              {isToday(currentDate) && (
                <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-bold">
                  TODAY
                </span>
              )}
            </div>
          </div>
        </div>
        
        {/* Time Grid */}
        <div className="max-h-[500px] overflow-y-auto">
          {timeSlots.map((slot, slotIdx) => {
            const slotEvents = dayEvents.filter(e => {
              const eventHour = parseInt(e.time?.split(':')[0]) || 8;
              const isPM = e.time?.includes('PM');
              const hour24 = isPM && eventHour !== 12 ? eventHour + 12 : eventHour;
              return hour24 === slot.hour;
            });
            
            const now = new Date();
            const isCurrentHour = isToday(currentDate) && now.getHours() === slot.hour;
            
            return (
              <div 
                key={slotIdx} 
                className={`flex border-b border-slate-800/30 ${isCurrentHour ? 'bg-emerald-500/10' : ''}`}
              >
                <div className="w-20 p-3 text-sm text-slate-500 text-right border-r border-slate-800 bg-slate-900/30 flex-shrink-0">
                  {slot.label}
                  {isCurrentHour && (
                    <div className="w-2 h-2 rounded-full bg-emerald-500 ml-auto mt-1"></div>
                  )}
                </div>
                <div 
                  className="flex-1 min-h-[60px] p-2 hover:bg-slate-800/30 cursor-pointer transition-colors"
                  onClick={() => handleDayClick(currentDate.getDate())}
                >
                  {slotEvents.map((event, eIdx) => (
                    <div 
                      key={eIdx}
                      className={`px-3 py-2 rounded-lg mb-2 ${event.color}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-white">{event.label}</span>
                        <span className="text-xs text-white/70">{event.time}</span>
                      </div>
                      {event.amount && (
                        <div className="text-xs text-white/80 mt-1">
                          Amount: {formatCurrency(event.amount)}
                        </div>
                      )}
                      {event.milestone?.assigned_sub_name && (
                        <div className="text-xs text-white/80 mt-1">
                          Crew: {event.milestone.assigned_sub_name}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* All Day Events Section */}
        {dayEvents.length > 0 && (
          <div className="p-4 border-t border-slate-800 bg-slate-800/20">
            <h5 className="text-sm font-bold text-slate-400 mb-3">ALL EVENTS TODAY</h5>
            <div className="space-y-2">
              {dayEvents.map((event, idx) => (
                <div key={idx} className={`flex items-center gap-3 p-3 rounded-lg ${event.color}/20 border border-slate-700`}>
                  <div className={`w-3 h-3 rounded-full ${event.color}`}></div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">{event.label}</p>
                    <p className="text-xs text-slate-400">{event.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };
  
  // Render Month View (existing)
  const renderMonthView = () => (
    <div className="bg-slate-900/50 rounded-xl border border-slate-800 overflow-hidden">
      {/* Day Headers */}
      <div className="grid grid-cols-7 border-b border-slate-800">
        {dayNames.map(day => (
          <div key={day} className="p-3 text-center text-xs font-bold text-slate-500 uppercase">
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar Days */}
      <div className="grid grid-cols-7">
        {calendarDays.map((day, idx) => {
          const events = day ? getEventsForDay(day) : [];
          const dayIsToday = day && 
            new Date().getDate() === day && 
            new Date().getMonth() === currentDate.getMonth() && 
            new Date().getFullYear() === currentDate.getFullYear();
          
          return (
            <div 
              key={idx}
              onClick={() => handleDayClick(day)}
              className={`min-h-[100px] p-2 border-b border-r border-slate-800/50 transition-colors ${
                day ? 'hover:bg-slate-800/50 cursor-pointer' : 'bg-slate-900/30'
              } ${selectedDay === day ? 'bg-slate-800/70' : ''}`}
            >
              {day && (
                <>
                  <div className={`text-sm font-medium mb-1 ${
                    dayIsToday ? 'w-7 h-7 rounded-full bg-emerald-500 text-white flex items-center justify-center' : 'text-slate-400'
                  }`}>
                    {day}
                  </div>
                  <div className="space-y-1">
                    {events.slice(0, 3).map((event, eIdx) => (
                      <div 
                        key={eIdx}
                        className={`text-xs px-1.5 py-0.5 rounded truncate text-white ${event.color}`}
                      >
                        {event.shortLabel || event.label}
                      </div>
                    ))}
                    {events.length > 3 && (
                      <div className="text-xs text-slate-500">+{events.length - 3} more</div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
  
  return (
    <div className="space-y-4">
      {/* Calendar Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={navigatePrev}
            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-all"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h3 className="text-xl font-bold text-white min-w-[280px] text-center">
            {getHeaderTitle()}
          </h3>
          <button 
            onClick={navigateNext}
            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-all"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          <button 
            onClick={goToToday}
            className="px-3 py-1.5 rounded-lg bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 text-sm font-medium transition-all"
          >
            Today
          </button>
        </div>
        
        <div className="flex items-center gap-1 bg-slate-800/50 rounded-lg p-1">
          {['month', 'week', 'day'].map((mode) => (
            <button 
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-4 py-1.5 rounded text-sm font-medium transition-all capitalize ${
                viewMode === mode 
                  ? 'bg-emerald-600 text-white' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-700'
              }`}
            >
              {mode}
            </button>
          ))}
        </div>
      </div>
      
      {/* Calendar Content based on view mode */}
      {viewMode === 'month' && renderMonthView()}
      {viewMode === 'week' && renderWeekView()}
      {viewMode === 'day' && renderDayView()}
      
      {/* Legend */}
      <div className="flex items-center gap-6 justify-center pt-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-teal-500"></div>
          <span className="text-xs text-slate-400">Milestone</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-emerald-500"></div>
          <span className="text-xs text-slate-400">Complete</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-amber-500"></div>
          <span className="text-xs text-slate-400">Payment</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-purple-500"></div>
          <span className="text-xs text-slate-400">Crew</span>
        </div>
      </div>
    </div>
  );
};

// ================= PAYMENT SCHEDULE ITEM =================

const PaymentScheduleItem = ({ payment, onSendInvoice, onMarkPaid, onViewInvoice, onSendReminder }) => {
  // Calculate if overdue
  const daysOverdue = payment.status === 'invoiced' ? getDaysUntil(payment.due_date) : 0;
  const isOverdue = daysOverdue < 0;
  const displayStatus = isOverdue ? 'overdue' : payment.status;
  
  // Determine border color - all teal/green to match mockup
  const borderColor = 'border-teal-500';
  
  // Get note text for pending items
  const getPendingNote = () => {
    if (payment.type === 'down_payment') return 'Ready to invoice';
    if (payment.linked_milestone_id) return 'Invoice after milestone completion';
    return 'Ready to invoice';
  };
  
  return (
    <div className={`p-4 rounded-lg bg-slate-800/30 border-l-4 ${borderColor}`}>
      {/* Top Row: Title, Badges, Amount */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          {/* Title and Badges */}
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-semibold text-white">{payment.description}</span>
            <span className="px-2 py-0.5 rounded text-xs font-bold bg-slate-700 text-slate-300">
              {payment.percentage}%
            </span>
            {payment.invoice_number && (
              <span className="px-2 py-0.5 rounded text-xs font-mono bg-slate-700/50 text-slate-400">
                {payment.invoice_number}
              </span>
            )}
          </div>
          {/* Due Date and Milestone */}
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span>Due: {formatDate(payment.due_date)}</span>
            <span>•</span>
            {payment.linked_milestone_title ? (
              <span>Milestone: {payment.linked_milestone_title}</span>
            ) : (
              <span>Not linked to milestone</span>
            )}
          </div>
        </div>
        
        {/* Amount */}
        <div className="text-right">
          <p className="text-2xl font-bold text-white">{formatCurrency(payment.amount)}</p>
        </div>
      </div>
      
      {/* Bottom Row: Status and Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-slate-700/50">
        {/* Status Badge and Info */}
        <div className="flex items-center gap-3">
          {displayStatus === 'paid' && (
            <>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-bold bg-emerald-500/20 text-emerald-400">
                <Check className="h-3.5 w-3.5" /> PAID
              </span>
              <span className="text-xs text-slate-500">Paid: {formatDate(payment.paid_date)}</span>
            </>
          )}
          
          {displayStatus === 'overdue' && (
            <>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-bold bg-amber-500/20 text-amber-400">
                <AlertTriangle className="h-3.5 w-3.5" /> OVERDUE
              </span>
              <span className="text-xs text-red-400 font-medium">{Math.abs(daysOverdue)} {Math.abs(daysOverdue) === 1 ? 'day' : 'days'} overdue</span>
            </>
          )}
          
          {displayStatus === 'invoiced' && !isOverdue && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-bold bg-blue-500/20 text-blue-400">
              INVOICED
            </span>
          )}
          
          {displayStatus === 'pending' && (
            <>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-bold bg-slate-600/50 text-slate-400">
                <Clock className="h-3.5 w-3.5" /> PENDING
              </span>
              <span className="text-xs text-slate-500">{getPendingNote()}</span>
            </>
          )}
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {displayStatus === 'paid' && (
            <button 
              onClick={() => onViewInvoice(payment)}
              className="px-3 py-1.5 rounded text-sm font-medium bg-slate-700 hover:bg-slate-600 text-slate-300 transition-all"
            >
              View Invoice
            </button>
          )}
          
          {displayStatus === 'overdue' && (
            <>
              <button 
                onClick={() => onViewInvoice(payment)}
                className="px-3 py-1.5 rounded text-sm font-medium bg-slate-700 hover:bg-slate-600 text-slate-300 transition-all"
              >
                View Invoice
              </button>
              <button 
                onClick={() => onMarkPaid(payment)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-bold bg-emerald-600 hover:bg-emerald-500 text-white transition-all"
              >
                <Check className="h-3.5 w-3.5" /> Mark Paid
              </button>
              <button 
                onClick={() => onSendReminder && onSendReminder(payment)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-bold bg-amber-600 hover:bg-amber-500 text-white transition-all"
              >
                <AlertTriangle className="h-3.5 w-3.5" /> Send Reminder
              </button>
            </>
          )}
          
          {displayStatus === 'invoiced' && !isOverdue && (
            <>
              <button 
                onClick={() => onViewInvoice(payment)}
                className="px-3 py-1.5 rounded text-sm font-medium bg-slate-700 hover:bg-slate-600 text-slate-300 transition-all"
              >
                View Invoice
              </button>
              <button 
                onClick={() => onMarkPaid(payment)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-bold bg-emerald-600 hover:bg-emerald-500 text-white transition-all"
              >
                <Check className="h-3.5 w-3.5" /> Mark Paid
              </button>
            </>
          )}
          
          {displayStatus === 'pending' && (
            <button 
              onClick={() => onSendInvoice(payment)}
              className="px-3 py-1.5 rounded text-sm font-bold bg-teal-600 hover:bg-teal-500 text-white transition-all"
            >
              Send Invoice
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ================= SEND INVOICE MODAL =================

const SendInvoiceModal = ({ payment, projectId, onClose, onSent }) => {
  const [loading, setLoading] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [recipient, setRecipient] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  
  useEffect(() => {
    generateInvoice();
  }, [payment.payment_id]);
  
  const generateInvoice = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/payments/${payment.payment_id}/generate-invoice`,
        {},
        { withCredentials: true }
      );
      setInvoice(res.data);
      setRecipient(res.data.client_email || '');
      setMessage(`Dear ${res.data.client_name || 'Client'},\n\nPlease find attached invoice ${res.data.invoice_number} for ${payment.description} in the amount of ${formatCurrency(payment.amount)}.\n\nPayment is due by ${formatDate(payment.due_date)}.\n\nThank you!`);
    } catch (error) {
      toast.error("Failed to generate invoice");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSend = async () => {
    if (!recipient) {
      toast.error("Please enter recipient email");
      return;
    }
    
    setSending(true);
    try {
      await axios.post(
        `${API_BASE}/api/invoices/${invoice.invoice_id}/send`,
        { recipient, message },
        { withCredentials: true }
      );
      toast.success("Invoice sent successfully!");
      onSent();
    } catch (error) {
      toast.error("Failed to send invoice");
      console.error(error);
    } finally {
      setSending(false);
    }
  };
  
  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-lg rounded-xl bg-slate-900 border border-slate-800 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 bg-slate-900/50 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white uppercase tracking-wide">Send Invoice</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-800 text-slate-400">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Body */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
            </div>
          ) : invoice ? (
            <div className="space-y-4">
              {/* Invoice Preview */}
              <div className="p-4 rounded-lg bg-slate-800 border border-slate-700">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Invoice</p>
                    <p className="text-lg font-mono font-bold text-white">{invoice.invoice_number}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500 uppercase">Amount</p>
                    <p className="text-2xl font-mono font-bold text-emerald-400">{formatCurrency(invoice.total)}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">To:</p>
                    <p className="text-white">{invoice.client_name}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Due:</p>
                    <p className="text-white">{formatDate(invoice.due_date)}</p>
                  </div>
                </div>
              </div>
              
              {/* Recipient */}
              <div>
                <label className="block text-xs text-slate-500 uppercase tracking-wider mb-2">Recipient Email</label>
                <input
                  type="email"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-amber-500"
                  placeholder="client@email.com"
                />
              </div>
              
              {/* Message */}
              <div>
                <label className="block text-xs text-slate-500 uppercase tracking-wider mb-2">Message</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-amber-500 resize-none"
                />
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              Failed to load invoice
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800 bg-slate-900/50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 font-medium transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending || !invoice}
            className="px-6 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white font-bold transition-all disabled:opacity-50 flex items-center gap-2"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? 'Sending...' : 'Send Invoice'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ================= MARK PAID MODAL =================

const MarkPaidModal = ({ payment, onClose, onPaid }) => {
  const [paidDate, setPaidDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState('check');
  const [checkNumber, setCheckNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API_BASE}/api/payments/${payment.payment_id}/mark-paid`,
        { paid_date: paidDate, payment_method: paymentMethod, check_number: checkNumber, notes },
        { withCredentials: true }
      );
      toast.success("Payment recorded!");
      onPaid();
    } catch (error) {
      toast.error("Failed to record payment");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-md rounded-xl bg-slate-900 border border-slate-800 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 bg-slate-900/50 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white uppercase tracking-wide">Record Payment</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-800 text-slate-400">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Payment Summary */}
          <div className="p-4 rounded-lg bg-slate-800 border border-slate-700">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xs text-slate-500">Invoice</p>
                <p className="font-mono text-white">{payment.invoice_number || 'N/A'}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Amount</p>
                <p className="text-xl font-mono font-bold text-emerald-400">{formatCurrency(payment.amount)}</p>
              </div>
            </div>
          </div>
          
          {/* Payment Date */}
          <div>
            <label className="block text-xs text-slate-500 uppercase tracking-wider mb-2">Payment Date</label>
            <input
              type="date"
              value={paidDate}
              onChange={(e) => setPaidDate(e.target.value)}
              className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
            />
          </div>
          
          {/* Payment Method */}
          <div>
            <label className="block text-xs text-slate-500 uppercase tracking-wider mb-2">Payment Method</label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
            >
              <option value="check">Check</option>
              <option value="ach">ACH / Bank Transfer</option>
              <option value="credit_card">Credit Card</option>
              <option value="cash">Cash</option>
              <option value="other">Other</option>
            </select>
          </div>
          
          {/* Check Number */}
          {paymentMethod === 'check' && (
            <div>
              <label className="block text-xs text-slate-500 uppercase tracking-wider mb-2">Check Number</label>
              <input
                type="text"
                value={checkNumber}
                onChange={(e) => setCheckNumber(e.target.value)}
                placeholder="Check #"
                className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-600"
              />
            </div>
          )}
          
          {/* Notes */}
          <div>
            <label className="block text-xs text-slate-500 uppercase tracking-wider mb-2">Notes (Optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Any additional notes..."
              className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 resize-none placeholder-slate-600"
            />
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800 bg-slate-900/50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 font-medium transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-6 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white font-bold transition-all disabled:opacity-50 flex items-center gap-2"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {submitting ? 'Recording...' : 'Record Payment'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ================= ADD CALENDAR EVENT MODAL =================

const AddEventModal = ({ date, projectId, milestones, onClose, onSaved }) => {
  const [eventType, setEventType] = useState('milestone_start');
  const [title, setTitle] = useState('');
  const [linkedMilestone, setLinkedMilestone] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Enhanced fields
  const [isAllDay, setIsAllDay] = useState(false);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [location, setLocation] = useState('');
  const [reminder, setReminder] = useState('30');
  const [eventColor, setEventColor] = useState('teal');
  const [attendees, setAttendees] = useState([]);
  const [newAttendee, setNewAttendee] = useState('');
  const [recurrence, setRecurrence] = useState('none');
  
  // Available colors
  const colorOptions = [
    { id: 'teal', label: 'Teal', class: 'bg-teal-500' },
    { id: 'emerald', label: 'Green', class: 'bg-emerald-500' },
    { id: 'blue', label: 'Blue', class: 'bg-blue-500' },
    { id: 'purple', label: 'Purple', class: 'bg-purple-500' },
    { id: 'amber', label: 'Yellow', class: 'bg-amber-500' },
    { id: 'red', label: 'Red', class: 'bg-red-500' },
  ];
  
  // Reminder options
  const reminderOptions = [
    { value: '0', label: 'At time of event' },
    { value: '15', label: '15 minutes before' },
    { value: '30', label: '30 minutes before' },
    { value: '60', label: '1 hour before' },
    { value: '1440', label: '1 day before' },
    { value: 'none', label: 'No reminder' },
  ];
  
  // Recurrence options
  const recurrenceOptions = [
    { value: 'none', label: 'Does not repeat' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'biweekly', label: 'Every 2 weeks' },
    { value: 'monthly', label: 'Monthly' },
  ];
  
  const addAttendee = () => {
    if (newAttendee.trim() && !attendees.includes(newAttendee.trim())) {
      setAttendees([...attendees, newAttendee.trim()]);
      setNewAttendee('');
    }
  };
  
  const removeAttendee = (attendee) => {
    setAttendees(attendees.filter(a => a !== attendee));
  };
  
  const handleSubmit = async () => {
    if (!title) {
      toast.error("Please enter a title");
      return;
    }
    
    setSubmitting(true);
    try {
      // For now, we'll update milestone dates if linked
      if (linkedMilestone && (eventType === 'milestone_start' || eventType === 'milestone_end')) {
        const updateData = eventType === 'milestone_start' 
          ? { start_date: date }
          : { target_date: date };
        
        await axios.patch(
          `${API_BASE}/api/milestones/${linkedMilestone}`,
          updateData,
          { withCredentials: true }
        );
      }
      
      toast.success("Event added to calendar!");
      onSaved();
    } catch (error) {
      toast.error("Failed to add event");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-lg rounded-xl bg-slate-900 border border-slate-800 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-800 bg-slate-900/50 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg bg-${eventColor}-500/20 flex items-center justify-center`}>
              <Calendar className={`h-5 w-5 text-${eventColor}-400`} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Add Event</h3>
              <p className="text-sm text-slate-400">{formatDate(date)}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Event Type */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Event Type</label>
            <select
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
            >
              <option value="milestone_start">Milestone Start</option>
              <option value="milestone_end">Milestone Due/Complete</option>
              <option value="inspection">Inspection</option>
              <option value="delivery">Delivery</option>
              <option value="meeting">Meeting</option>
              <option value="walkthrough">Site Walkthrough</option>
              <option value="permit">Permit Pickup/Submission</option>
              <option value="crew_start">Crew Start Date</option>
              <option value="payment">Payment Due</option>
              <option value="other">Other</option>
            </select>
          </div>
          
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Foundation inspection, Client meeting"
              className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
            />
          </div>
          
          {/* All Day Toggle & Time */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-slate-300">All Day Event</label>
              <button
                type="button"
                onClick={() => setIsAllDay(!isAllDay)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  isAllDay ? 'bg-emerald-600' : 'bg-slate-700'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isAllDay ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            
            {!isAllDay && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">End Time</label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* Link to Milestone (conditional) */}
          {(eventType === 'milestone_start' || eventType === 'milestone_end') && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Link to Milestone</label>
              <select
                value={linkedMilestone}
                onChange={(e) => setLinkedMilestone(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
              >
                <option value="">Select milestone...</option>
                {milestones.map(m => (
                  <option key={m.milestone_id} value={m.milestone_id}>{m.title}</option>
                ))}
              </select>
            </div>
          )}
          
          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Location</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Job site address or meeting location"
                className="w-full pl-10 pr-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
              />
            </div>
          </div>
          
          {/* Attendees/Crew */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Attendees / Crew</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newAttendee}
                onChange={(e) => setNewAttendee(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAttendee())}
                placeholder="Add person or crew"
                className="flex-1 px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
              />
              <button
                type="button"
                onClick={addAttendee}
                className="px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium transition-colors"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            {attendees.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {attendees.map((attendee, idx) => (
                  <span 
                    key={idx}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-slate-800 text-sm text-slate-300"
                  >
                    <Users className="h-3 w-3" />
                    {attendee}
                    <button onClick={() => removeAttendee(attendee)} className="ml-1 hover:text-red-400">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          
          {/* Reminder & Recurrence */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Reminder</label>
              <select
                value={reminder}
                onChange={(e) => setReminder(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
              >
                {reminderOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Repeat</label>
              <select
                value={recurrence}
                onChange={(e) => setRecurrence(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
              >
                {recurrenceOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Event Color */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Event Color</label>
            <div className="flex gap-2">
              {colorOptions.map(color => (
                <button
                  key={color.id}
                  type="button"
                  onClick={() => setEventColor(color.id)}
                  className={`w-8 h-8 rounded-full ${color.class} transition-all ${
                    eventColor === color.id 
                      ? 'ring-2 ring-white ring-offset-2 ring-offset-slate-900 scale-110' 
                      : 'opacity-60 hover:opacity-100'
                  }`}
                  title={color.label}
                />
              ))}
            </div>
          </div>
          
          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Any additional details, special instructions, or requirements..."
              className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 resize-none placeholder-slate-500"
            />
          </div>
        </div>
        
        <div className="px-6 py-4 border-t border-slate-800 bg-slate-900/50 flex justify-end gap-3 flex-shrink-0">
          <button onClick={onClose} className="px-5 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 font-medium transition-all">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !title}
            className={`px-6 py-2.5 rounded-lg bg-${eventColor}-600 hover:bg-${eventColor}-500 text-white font-bold transition-all disabled:opacity-50 flex items-center gap-2`}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {submitting ? 'Adding...' : 'Add Event'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ================= VIEW INVOICE MODAL =================

const ViewInvoiceModal = ({ payment, onClose }) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPDF = async () => {
    if (!payment.invoice_id) {
      toast.error("No invoice ID available");
      return;
    }
    
    setDownloading(true);
    try {
      // Try the active_operations endpoint first (for invoice_xxx format)
      // Falls back to client-invoices endpoint (for inv_xxx format)
      let response;
      try {
        response = await axios.get(
          `${API_BASE}/api/invoices/${payment.invoice_id}/pdf`,
          { responseType: 'blob', withCredentials: true }
        );
      } catch (firstError) {
        // Fallback to client-invoices endpoint
        response = await axios.get(
          `${API_BASE}/api/client-invoices/${payment.invoice_id}/download-pdf`,
          { responseType: 'blob', withCredentials: true }
        );
      }
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${payment.invoice_number || 'download'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Invoice downloaded successfully!");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download invoice PDF");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-lg rounded-xl bg-slate-900 border border-slate-800 shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-800 bg-slate-900/50 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">Invoice {payment.invoice_number}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-800 text-slate-400">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-6">
          {/* Invoice Preview */}
          <div className="bg-white rounded-lg p-6 text-slate-900">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h4 className="text-xl font-bold text-slate-900">INVOICE</h4>
                <p className="text-sm text-slate-600">{payment.invoice_number}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-600">Date Issued</p>
                <p className="font-medium">{formatDate(payment.sent_date || new Date().toISOString())}</p>
              </div>
            </div>
            
            <div className="border-t border-slate-200 pt-4 mb-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Bill To</p>
                  <p className="font-medium">{payment.client_name || 'Client'}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500 uppercase">Due Date</p>
                  <p className="font-medium">{formatDate(payment.due_date)}</p>
                </div>
              </div>
            </div>
            
            <div className="border-t border-slate-200 pt-4">
              <div className="flex justify-between py-2">
                <span className="text-slate-600">{payment.description}</span>
                <span className="font-medium">{formatCurrency(payment.amount)}</span>
              </div>
              <div className="border-t border-slate-200 mt-4 pt-4 flex justify-between">
                <span className="text-lg font-bold">Total Due</span>
                <span className="text-lg font-bold text-emerald-600">{formatCurrency(payment.amount)}</span>
              </div>
            </div>
            
            {payment.status === 'paid' && (
              <div className="mt-6 p-4 bg-emerald-50 rounded-lg border border-emerald-200 text-center">
                <Check className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
                <p className="font-bold text-emerald-700">PAID</p>
                <p className="text-sm text-emerald-600">{formatDate(payment.paid_date)}</p>
              </div>
            )}
          </div>
        </div>
        
        <div className="px-6 py-4 border-t border-slate-800 bg-slate-900/50 flex justify-between">
          <button 
            onClick={handleDownloadPDF}
            disabled={downloading || !payment.invoice_id}
            className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-medium transition-all flex items-center gap-2"
            data-testid="download-invoice-pdf-btn"
          >
            {downloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileText className="h-4 w-4" />
            )}
            {downloading ? 'Downloading...' : 'Download PDF'}
          </button>
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 font-medium transition-all">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ================= MAIN PROJECT DETAIL PAGE =================

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { can } = usePermissions();
  
  // State
  const [project, setProject] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [costs, setCosts] = useState([]);
  const [paymentSchedule, setPaymentSchedule] = useState([]);
  const [scheduleStatus, setScheduleStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timelineView, setTimelineView] = useState('week');
  
  // Modal State
  const [sendInvoiceModal, setSendInvoiceModal] = useState(null);
  const [markPaidModal, setMarkPaidModal] = useState(null);
  const [addProgressModal, setAddProgressModal] = useState(false);
  const [addCostModal, setAddCostModal] = useState(false);
  const [addEventModal, setAddEventModal] = useState(null);
  const [viewInvoiceModal, setViewInvoiceModal] = useState(null);
  const [addMilestoneModal, setAddMilestoneModal] = useState(false);
  const [addPaymentModal, setAddPaymentModal] = useState(false);
  const [assignCrewModal, setAssignCrewModal] = useState(null); // milestone object
  const [editMilestoneModal, setEditMilestoneModal] = useState(null); // milestone object for editing
  const [bulkEditModal, setBulkEditModal] = useState(null); // array of milestone objects for bulk editing
  
  // Subcontractors for assignment
  const [subcontractors, setSubcontractors] = useState([]);
  
  // Load subcontractors
  const loadSubcontractors = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/subcontractors`, { withCredentials: true });
      setSubcontractors(res.data || []);
    } catch (err) {
      console.error("Failed to load subcontractors:", err);
    }
  }, []);
  
  // Load all project data
  const loadProjectData = useCallback(async () => {
    if (!projectId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const [projectRes, milestonesRes, costsRes, scheduleRes, statusRes] = await Promise.all([
        axios.get(`${API_BASE}/api/projects/${projectId}`, { withCredentials: true }),
        axios.get(`${API_BASE}/api/projects/${projectId}/milestones`, { withCredentials: true }),
        axios.get(`${API_BASE}/api/projects/${projectId}/costs`, { withCredentials: true }),
        axios.get(`${API_BASE}/api/projects/${projectId}/payment-schedule`, { withCredentials: true }),
        axios.get(`${API_BASE}/api/projects/${projectId}/schedule-status`, { withCredentials: true }),
      ]);
      
      setProject(projectRes.data);
      setMilestones(milestonesRes.data || []);
      setCosts(costsRes.data || []);
      setPaymentSchedule(scheduleRes.data || []);
      setScheduleStatus(statusRes.data);
    } catch (err) {
      console.error("Error loading project:", err);
      setError("Failed to load project data");
      toast.error("Failed to load project data");
    } finally {
      setLoading(false);
    }
  }, [projectId]);
  
  useEffect(() => {
    loadProjectData();
    loadSubcontractors();
  }, [loadProjectData, loadSubcontractors]);
  
  // Handlers
  const handleMilestoneClick = (milestone) => {
    // Open the edit modal instead of showing a toast
    setEditMilestoneModal(milestone);
  };
  
  const handleSendInvoice = (payment) => {
    setSendInvoiceModal(payment);
  };
  
  const handleMarkPaid = (payment) => {
    setMarkPaidModal(payment);
  };
  
  const handleViewInvoice = (payment) => {
    setViewInvoiceModal(payment);
  };
  
  const handleAddProgress = () => {
    setAddProgressModal(true);
  };
  
  const handleAddCost = () => {
    setAddCostModal(true);
  };
  
  const handleAddEvent = (date) => {
    setAddEventModal(date);
  };
  
  const handleAddMilestone = () => {
    setAddMilestoneModal(true);
  };
  
  const handleAddPayment = () => {
    setAddPaymentModal(true);
  };
  
  // Calculate metrics
  const invoiced = paymentSchedule
    .filter(p => ['invoiced', 'paid', 'overdue'].includes(p.status))
    .reduce((sum, p) => sum + (p.amount || 0), 0);
  
  const collected = paymentSchedule
    .filter(p => p.status === 'paid')
    .reduce((sum, p) => sum + (p.amount || 0), 0);
  
  const outstanding = invoiced - collected;
  const spentPercent = project?.budget > 0 ? Math.round(((project?.spent || 0) / project.budget) * 100) : 0;
  const collectedPercent = project?.budget > 0 ? Math.round((collected / project.budget) * 100) : 0;
  
  // Render
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-amber-500 mx-auto mb-4" />
          <p className="text-slate-400">Loading project...</p>
        </div>
      </div>
    );
  }
  
  if (error || !project) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-white text-lg mb-2">Project not found</p>
          <button
            onClick={() => navigate('/active')}
            className="px-4 py-2 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700"
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }
  
  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'timeline', label: 'Timeline' },
    { id: 'financials', label: 'Financials' },
    { id: 'payments', label: 'Payments' },
    { id: 'purchase-orders', label: 'Purchase Orders' },
    { id: 'crew', label: 'Crew' },
    { id: 'field-intel', label: 'Field Intel' },
  ];
  
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-6 py-4">
          {/* Back Button */}
          <button
            onClick={() => navigate('/projects')}
            className="flex items-center gap-2 text-slate-400 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Back to Projects</span>
          </button>
          
          {/* Project Header */}
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-emerald-400">{project.client_name} - {project.name}</h1>
              {project.job_address && (
                <p className="text-slate-400 text-sm mt-1">{project.job_address}</p>
              )}
            </div>
            
            {scheduleStatus && (
              <ScheduleStatusBadge 
                status={scheduleStatus.schedule_status} 
                daysVariance={scheduleStatus.days_variance} 
              />
            )}
          </div>
          
          {/* Tabs - Simple underline style */}
          <div className="flex gap-8 mt-6 border-b border-slate-800">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 text-sm font-medium transition-all relative ${
                  activeTab === tab.id
                    ? 'text-emerald-400'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-400" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Add Progress Button - Top Right (Permission: update_milestone_progress) */}
            {can('update_milestone_progress') && (
              <div className="flex justify-end">
                <button 
                  onClick={handleAddProgress}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium transition-all"
                >
                  <Plus className="h-4 w-4" />
                  Add Progress
                </button>
              </div>
            )}
            
            {/* Metrics Grid - 3 cards */}
            <div className="grid grid-cols-3 gap-4">
              <MetricCard
                label="Budget"
                value={formatCurrency(project.budget)}
                valueClass="text-white"
              />
              <MetricCard
                label="Spent"
                value={formatCurrency(project.spent || 0)}
                subtext={`${spentPercent}% of budget`}
                valueClass="text-amber-400"
              />
              <MetricCard
                label="Collected"
                value={formatCurrency(collected)}
                subtext={`${collectedPercent}% collected`}
                valueClass="text-emerald-400"
              />
            </div>
            
            {/* Project Timeline - Full Height */}
            <div className="bg-slate-900/50 rounded-xl border border-slate-800 overflow-hidden flex flex-col" style={{ minHeight: 'calc(100vh - 480px)' }}>
              <div className="px-6 py-4 border-b border-slate-800/50 flex items-center justify-between flex-shrink-0">
                <h2 className="text-lg font-bold text-white">Project Timeline</h2>
                <div className="flex items-center gap-1">
                  <button 
                    onClick={() => setTimelineView('week')}
                    className={`px-4 py-1.5 rounded text-sm font-medium transition-all ${
                      timelineView === 'week' 
                        ? 'bg-emerald-600 text-white' 
                        : 'bg-slate-800 text-slate-400 hover:text-white'
                    }`}
                  >
                    Week
                  </button>
                  <button 
                    onClick={() => setTimelineView('month')}
                    className={`px-4 py-1.5 rounded text-sm font-medium transition-all ${
                      timelineView === 'month' 
                        ? 'bg-emerald-600 text-white' 
                        : 'bg-slate-800 text-slate-400 hover:text-white'
                    }`}
                  >
                    Month
                  </button>
                  <button 
                    onClick={() => setTimelineView('quarter')}
                    className={`px-4 py-1.5 rounded text-sm font-medium transition-all ${
                      timelineView === 'quarter' 
                        ? 'bg-emerald-600 text-white' 
                        : 'bg-slate-800 text-slate-400 hover:text-white'
                    }`}
                  >
                    Quarter
                  </button>
                </div>
              </div>
              <div className="p-6 flex-1">
                <GanttTimeline
                  milestones={milestones}
                  projectStart={project.start_date}
                  projectEnd={project.estimated_completion_date}
                  onMilestoneClick={handleMilestoneClick}
                  onBulkEdit={(selectedMilestones) => setBulkEditModal(selectedMilestones)}
                  viewMode={timelineView}
                />
              </div>
            </div>
            
            {/* Action Items - Full Width with more height */}
            <div className="bg-slate-900/50 rounded-xl border border-slate-800 overflow-hidden" style={{ minHeight: '280px' }}>
              <div className="px-6 py-4 border-b border-slate-800/50">
                <h2 className="text-lg font-bold text-white">Action Items</h2>
              </div>
              <ActionItems milestones={milestones} project={project} />
            </div>
          </div>
        )}
        
        {/* TIMELINE TAB */}
        {activeTab === 'timeline' && (
          <div className="space-y-6">
            {/* Header with Add Progress button */}
            <div className="flex items-center justify-end">
              <button 
                onClick={handleAddProgress}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium transition-all"
              >
                <Plus className="h-4 w-4" />
                Add Progress
              </button>
            </div>
            
            {/* Calendar View */}
            <CalendarView 
              milestones={milestones}
              paymentSchedule={paymentSchedule}
              project={project}
              onDayClick={(date, events) => {
                handleAddEvent(date);
              }}
            />
          </div>
        )}
        
        {/* FINANCIALS TAB */}
        {activeTab === 'financials' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white">Financial Tracking</h2>
                <p className="text-slate-400 text-sm mt-1">Cost breakdown, transactions, and profit margins</p>
              </div>
              <div className="flex items-center gap-3">
                {can('mark_payment_received') && (
                  <button 
                    onClick={handleAddPayment}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium transition-all"
                  >
                    <Plus className="h-4 w-4" />
                    Add Payment
                  </button>
                )}
                {can('submit_costs') && (
                  <button 
                    onClick={handleAddCost}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium transition-all"
                  >
                    <Plus className="h-4 w-4" />
                    Add Cost
                  </button>
                )}
                {can('view_job_costing') && (
                  <button 
                    onClick={() => navigate('/job-costing')}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white font-medium border border-slate-700 transition-all"
                  >
                    <BarChart3 className="h-4 w-4" />
                    Job Costing
                  </button>
                )}
              </div>
            </div>
            
            {/* Metrics */}
            <div className="grid grid-cols-4 gap-4">
              <MetricCard label="Budget" value={formatCurrency(project.budget)} valueClass="text-white" />
              <MetricCard 
                label="Spent" 
                value={formatCurrency(project.spent || 0)} 
                subtext={`${spentPercent}% of budget`}
                valueClass="text-amber-400"
              />
              <MetricCard 
                label="Remaining" 
                value={formatCurrency(project.budget - (project.spent || 0))}
                valueClass="text-emerald-400"
              />
              <MetricCard 
                label="Margin" 
                value={`${Math.round(((project.budget - (project.spent || 0)) / project.budget) * 100)}%`}
                valueClass="text-blue-400"
              />
            </div>
            
            {/* Costs by Milestone */}
            <div className="bg-slate-900/50 rounded-xl border border-slate-800 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-800">
                <h2 className="text-lg font-bold text-white">Cost Breakdown by Milestone</h2>
              </div>
              <div className="p-6">
                {milestones.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No milestones yet</p>
                ) : (
                  <div className="space-y-4">
                    {milestones.map(milestone => (
                      <div key={milestone.milestone_id} className="p-4 rounded-lg bg-slate-800/30">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-medium text-white">{milestone.title}</h3>
                          <div className="text-right">
                            <span className="font-medium text-emerald-400">{formatCurrency(milestone.actual_cost || 0)}</span>
                            <span className="text-slate-500 text-sm"> / {formatCurrency(milestone.estimated_cost || 0)}</span>
                          </div>
                        </div>
                        <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${
                              (milestone.actual_cost || 0) > (milestone.estimated_cost || 0) 
                                ? 'bg-red-500' 
                                : 'bg-emerald-500'
                            }`}
                            style={{ 
                              width: `${Math.min(100, ((milestone.actual_cost || 0) / (milestone.estimated_cost || 1)) * 100)}%` 
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* PAYMENTS TAB */}
        {activeTab === 'payments' && (
          <div className="space-y-6">
            {/* Payment Metrics */}
            <div className="grid grid-cols-4 gap-4">
              <MetricCard 
                label="Total Contract" 
                value={formatCurrency(project.budget)} 
                valueClass="text-white"
              />
              <MetricCard 
                label="Invoiced" 
                value={formatCurrency(invoiced)} 
                subtext={project.budget > 0 ? `${Math.round((invoiced / project.budget) * 100)}% invoiced` : null}
                valueClass="text-amber-400" 
              />
              <MetricCard 
                label="Collected" 
                value={formatCurrency(collected)} 
                subtext={project.budget > 0 ? `${Math.round((collected / project.budget) * 100)}% collected` : null}
                valueClass="text-emerald-400" 
              />
              <MetricCard 
                label="Outstanding" 
                value={formatCurrency(outstanding)} 
                subtext={project.budget > 0 && outstanding > 0 ? `${Math.round((outstanding / project.budget) * 100)}% outstanding` : null}
                valueClass={outstanding > 0 ? 'text-red-400' : 'text-white'} 
              />
            </div>
            
            {/* Payment Schedule */}
            <div className="bg-slate-900/50 rounded-xl border border-slate-800 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-teal-400" />
                  <h2 className="text-lg font-bold text-white">Payment Schedule</h2>
                </div>
                <button className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium transition-all">
                  <Settings className="h-4 w-4" />
                  Edit Schedule
                </button>
              </div>
              <div className="p-6 space-y-4">
                {paymentSchedule.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No payment schedule configured</p>
                ) : (
                  paymentSchedule.map(payment => (
                    <PaymentScheduleItem
                      key={payment.payment_id}
                      payment={payment}
                      onSendInvoice={handleSendInvoice}
                      onMarkPaid={handleMarkPaid}
                      onViewInvoice={handleViewInvoice}
                      onSendReminder={(p) => toast.info("Reminder sent (mocked)")}
                    />
                  ))
                )}
              </div>
            </div>
            
            {/* Payment History */}
            <div className="bg-slate-900/50 rounded-xl border border-slate-800 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-800">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-slate-400" />
                  <h2 className="text-lg font-bold text-white">Payment History</h2>
                </div>
              </div>
              <div className="divide-y divide-slate-800/50">
                {paymentSchedule.filter(p => p.status === 'paid' || p.invoice_number).length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No payment history yet</p>
                ) : (
                  paymentSchedule
                    .filter(p => p.status === 'paid' || p.invoice_number)
                    .sort((a, b) => new Date(b.paid_date || b.sent_date || 0) - new Date(a.paid_date || a.sent_date || 0))
                    .slice(0, 10)
                    .map((payment, idx) => (
                      <React.Fragment key={idx}>
                        {payment.status === 'paid' && (
                          <div className="flex items-center gap-4 px-6 py-4 hover:bg-slate-800/30 transition-colors">
                            <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                              <Check className="h-5 w-5 text-emerald-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-white">Payment Received - {payment.invoice_number || 'N/A'}</p>
                              <p className="text-xs text-slate-500">
                                {formatDate(payment.paid_date)}
                                {payment.payment_method && ` • ${payment.payment_method === 'check' ? `Check #${payment.check_number || ''}` : payment.payment_method.toUpperCase()}`}
                              </p>
                            </div>
                            <div className="text-right">
                              <span className="text-emerald-400 font-bold">+{formatCurrency(payment.amount)}</span>
                            </div>
                          </div>
                        )}
                        {payment.invoice_number && payment.sent_date && (
                          <div className="flex items-center gap-4 px-6 py-4 hover:bg-slate-800/30 transition-colors">
                            <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                              <FileText className="h-5 w-5 text-blue-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-white">Invoice Sent - {payment.invoice_number}</p>
                              <p className="text-xs text-slate-500">{formatDate(payment.sent_date)} • Sent to {payment.recipient_email || 'client'}</p>
                            </div>
                            <div className="text-right">
                              <span className="text-slate-400">{formatCurrency(payment.amount)}</span>
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    ))
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* PURCHASE ORDERS TAB */}
        {activeTab === 'purchase-orders' && (
          <PurchaseOrdersTab 
            projectId={projectId} 
            milestones={milestones}
          />
        )}
        
        {/* CREW TAB */}
        {activeTab === 'crew' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white">Crew Management</h2>
                <p className="text-slate-400 text-sm mt-1">Assign subcontractors and manage crew for each milestone</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-2xl font-bold text-orange-400">
                    {milestones.filter(m => m.assigned_sub_name).length}
                  </div>
                  <div className="text-xs text-slate-500">Assigned</div>
                </div>
                <div className="h-10 w-px bg-slate-700"></div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-slate-400">
                    {milestones.filter(m => !m.assigned_sub_name).length}
                  </div>
                  <div className="text-xs text-slate-500">Unassigned</div>
                </div>
              </div>
            </div>
            
            {/* Crew Overview Cards */}
            {milestones.filter(m => m.assigned_sub_name).length > 0 && (
              <div className="bg-slate-900/50 rounded-xl border border-slate-800 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-800 flex items-center gap-2">
                  <HardHat className="h-5 w-5 text-orange-400" />
                  <h3 className="text-sm font-bold text-white uppercase tracking-wider">Active Crew Assignments</h3>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[...new Map(milestones.filter(m => m.assigned_sub_name).map(m => [m.assigned_sub_id || m.assigned_sub_name, m])).values()].map((milestone, idx) => {
                      const assignedMilestones = milestones.filter(m => 
                        (m.assigned_sub_id === milestone.assigned_sub_id) || 
                        (m.assigned_sub_name === milestone.assigned_sub_name)
                      );
                      const completedCount = assignedMilestones.filter(m => m.status === 'complete').length;
                      
                      return (
                        <div key={idx} className="p-4 rounded-xl bg-slate-800/50 border border-slate-700 hover:border-orange-500/50 transition-all">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                              {milestone.assigned_sub_name?.charAt(0).toUpperCase() || '?'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-bold text-white truncate">{milestone.assigned_sub_name}</p>
                              {milestone.assigned_sub_phone && (
                                <a 
                                  href={`tel:${milestone.assigned_sub_phone}`}
                                  className="text-sm text-emerald-400 hover:text-emerald-300 flex items-center gap-1 mt-1"
                                >
                                  📞 {milestone.assigned_sub_phone}
                                </a>
                              )}
                              <div className="flex items-center gap-2 mt-2">
                                <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-emerald-500 rounded-full transition-all"
                                    style={{ width: `${(completedCount / assignedMilestones.length) * 100}%` }}
                                  ></div>
                                </div>
                                <span className="text-xs text-slate-400">{completedCount}/{assignedMilestones.length}</span>
                              </div>
                              <div className="flex flex-wrap gap-1 mt-2">
                                {assignedMilestones.slice(0, 2).map(m => (
                                  <span key={m.milestone_id} className="text-xs px-2 py-0.5 rounded-full bg-slate-700 text-slate-300 truncate max-w-[120px]">
                                    {m.title}
                                  </span>
                                ))}
                                {assignedMilestones.length > 2 && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-slate-700 text-slate-400">
                                    +{assignedMilestones.length - 2} more
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            
            {/* All Milestones - Assignment Table */}
            <div className="bg-slate-900/50 rounded-xl border border-slate-800 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-emerald-400" />
                  <h3 className="text-sm font-bold text-white uppercase tracking-wider">Milestone Assignments</h3>
                </div>
              </div>
              
              {milestones.length === 0 ? (
                <div className="p-8 text-center">
                  <Users className="h-12 w-12 mx-auto mb-4 text-slate-600" />
                  <p className="text-slate-500">No milestones to assign crew to</p>
                  <p className="text-sm text-slate-600 mt-1">Create milestones first in the Overview tab</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-800">
                  {milestones.map((milestone) => (
                    <div key={milestone.milestone_id} className="px-6 py-4 hover:bg-slate-800/30 transition-colors">
                      <div className="flex items-center gap-4">
                        {/* Status Indicator */}
                        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                          milestone.status === 'complete' ? 'bg-emerald-500' :
                          milestone.status === 'in_progress' ? 'bg-blue-500' :
                          milestone.status === 'blocked' ? 'bg-red-500' :
                          'bg-slate-600'
                        }`}></div>
                        
                        {/* Milestone Info */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-white">{milestone.title}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-slate-500">
                              {new Date(milestone.start_date).toLocaleDateString()} - {new Date(milestone.target_date).toLocaleDateString()}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              milestone.status === 'complete' ? 'bg-emerald-500/20 text-emerald-400' :
                              milestone.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                              milestone.status === 'blocked' ? 'bg-red-500/20 text-red-400' :
                              'bg-slate-700 text-slate-400'
                            }`}>
                              {milestone.status?.replace(/_/g, ' ') || 'Not Started'}
                            </span>
                          </div>
                        </div>
                        
                        {/* Assigned Crew or Assign Button */}
                        <div className="flex items-center gap-3">
                          {milestone.assigned_sub_name ? (
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-500/10 border border-orange-500/30">
                                <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-sm">
                                  {milestone.assigned_sub_name.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-white">{milestone.assigned_sub_name}</p>
                                  {milestone.assigned_sub_phone && (
                                    <p className="text-xs text-slate-400">{milestone.assigned_sub_phone}</p>
                                  )}
                                </div>
                              </div>
                              {can('assign_crew_to_project') && (
                                <button
                                  onClick={() => setAssignCrewModal(milestone)}
                                  className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-all"
                                  title="Change assignment"
                                >
                                  <Settings className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          ) : (
                            can('assign_crew_to_project') && (
                              <button
                                onClick={() => setAssignCrewModal(milestone)}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600/20 hover:bg-orange-600/30 text-orange-400 font-medium transition-all border border-orange-500/30"
                              >
                                <Plus className="h-4 w-4" />
                                Assign Crew
                              </button>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl bg-slate-900/50 border border-slate-800">
                <div className="text-2xl font-bold text-white">{milestones.length}</div>
                <div className="text-sm text-slate-500">Total Milestones</div>
              </div>
              <div className="p-4 rounded-xl bg-slate-900/50 border border-slate-800">
                <div className="text-2xl font-bold text-orange-400">{milestones.filter(m => m.assigned_sub_name).length}</div>
                <div className="text-sm text-slate-500">Crew Assigned</div>
              </div>
              <div className="p-4 rounded-xl bg-slate-900/50 border border-slate-800">
                <div className="text-2xl font-bold text-emerald-400">{milestones.filter(m => m.status === 'complete').length}</div>
                <div className="text-sm text-slate-500">Completed</div>
              </div>
              <div className="p-4 rounded-xl bg-slate-900/50 border border-slate-800">
                <div className="text-2xl font-bold text-blue-400">{milestones.filter(m => m.status === 'in_progress').length}</div>
                <div className="text-sm text-slate-500">In Progress</div>
              </div>
            </div>
          </div>
        )}
        
        {/* FIELD INTEL TAB */}
        {activeTab === 'field-intel' && (
          <FieldIntelTab projectId={projectId} />
        )}
      </div>
      
      {/* Modals */}
      {sendInvoiceModal && (
        <SendInvoiceModal
          payment={sendInvoiceModal}
          projectId={projectId}
          onClose={() => setSendInvoiceModal(null)}
          onSent={() => {
            setSendInvoiceModal(null);
            loadProjectData();
          }}
        />
      )}
      
      {markPaidModal && (
        <MarkPaidModal
          payment={markPaidModal}
          onClose={() => setMarkPaidModal(null)}
          onPaid={() => {
            setMarkPaidModal(null);
            loadProjectData();
          }}
        />
      )}
      
      {addProgressModal && (
        <UpdateProgressModal
          milestones={milestones}
          projectId={projectId}
          onClose={() => setAddProgressModal(false)}
          onSaved={() => {
            setAddProgressModal(false);
            loadProjectData();
          }}
          onAddMilestone={() => {
            setAddProgressModal(false);
            setAddMilestoneModal(true);
          }}
        />
      )}
      
      {addMilestoneModal && (
        <AddMilestoneModal
          projectId={projectId}
          milestones={milestones}
          onClose={() => setAddMilestoneModal(false)}
          onSaved={() => {
            setAddMilestoneModal(false);
            loadProjectData();
          }}
        />
      )}
      
      {addCostModal && (
        <AddCostModal
          milestones={milestones}
          projectId={projectId}
          onClose={() => setAddCostModal(false)}
          onSaved={() => {
            setAddCostModal(false);
            loadProjectData();
          }}
        />
      )}
      
      {addPaymentModal && (
        <RecordClientPaymentModal
          project={project}
          paymentSchedule={paymentSchedule}
          projectId={projectId}
          onClose={() => setAddPaymentModal(false)}
          onSaved={() => {
            setAddPaymentModal(false);
            loadProjectData();
          }}
        />
      )}
      
      {addEventModal && (
        <AddEventModal
          date={addEventModal}
          projectId={projectId}
          milestones={milestones}
          onClose={() => setAddEventModal(null)}
          onSaved={() => {
            setAddEventModal(null);
            loadProjectData();
          }}
        />
      )}
      
      {viewInvoiceModal && (
        <ViewInvoiceModal
          payment={viewInvoiceModal}
          onClose={() => setViewInvoiceModal(null)}
        />
      )}
      
      {assignCrewModal && (
        <AssignCrewModal
          milestone={assignCrewModal}
          projectId={projectId}
          onClose={() => setAssignCrewModal(null)}
          onSaved={() => {
            setAssignCrewModal(null);
            loadProjectData();
          }}
        />
      )}
      
      {editMilestoneModal && (
        <EditMilestoneModal
          milestone={editMilestoneModal}
          projectId={projectId}
          subcontractors={subcontractors}
          onClose={() => setEditMilestoneModal(null)}
          onSaved={() => {
            setEditMilestoneModal(null);
            loadProjectData();
          }}
          onDeleted={() => {
            setEditMilestoneModal(null);
            loadProjectData();
          }}
        />
      )}
      
      {bulkEditModal && bulkEditModal.length > 0 && (
        <BulkEditMilestoneModal
          milestones={bulkEditModal}
          projectId={projectId}
          subcontractors={subcontractors}
          onClose={() => setBulkEditModal(null)}
          onSaved={() => {
            setBulkEditModal(null);
            loadProjectData();
          }}
        />
      )}
    </div>
  );
};

export default ProjectDetailPage;
